import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  BarChart3,
  CalendarClock,
  KanbanSquare,
  LayoutDashboard,
  ListChecks,
  LogOut,
  Users2,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/crm", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/crm/pipeline", label: "Pipeline", icon: KanbanSquare, testid: "nav-pipeline" },
  { to: "/crm/leads", label: "Leads", icon: ListChecks, testid: "nav-leads" },
  { to: "/crm/schedule", label: "Schedule", icon: CalendarClock, testid: "nav-schedule" },
  { to: "/crm/team", label: "Team", icon: Users2, testid: "nav-team", adminOnly: true },
  { to: "/crm/attribution", label: "Ads ROI", icon: BarChart3, testid: "nav-attribution", adminOnly: true },
];

export default function CrmShell({ title, subtitle, actions, children }) {
  const { user, logout, isAdmin } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const items = NAV.filter((n) => !n.adminOnly || isAdmin);

  const doLogout = async () => {
    await logout();
    navigate("/crm/login");
  };

  return (
    <div className="min-h-screen bg-cloud text-ink" data-testid="crm-shell">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-60 flex-col border-r border-line bg-white px-4 py-6 lg:flex">
        <Link to="/" className="px-2 font-display text-lg font-extrabold tracking-[-0.03em] text-ink">
          brickspace<span className="text-brand">.</span>
        </Link>
        <p className="mt-1 px-2 text-xs text-muted2">Lead console</p>

        <nav className="mt-8 flex-1 space-y-1">
          {items.map((n) => {
            const active = pathname === n.to;
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={n.testid}
                className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
                  active ? "bg-mint text-brandDark" : "text-body hover:bg-cloud hover:text-ink"
                }`}
              >
                <n.icon className="h-4 w-4" />
                {n.label}
              </Link>
            );
          })}
        </nav>

        <div className="rounded-xl border border-line bg-cloud p-3.5">
          <div className="truncate text-sm font-bold text-ink">{user?.name}</div>
          <div className="mt-0.5 text-xs font-semibold capitalize text-brandDark">{user?.role}</div>
          <button
            onClick={doLogout}
            data-testid="logout-btn"
            className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border border-line bg-white py-2 text-xs font-semibold text-body transition-colors hover:border-red-200 hover:text-red-600"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </aside>

      <header className="sticky top-0 z-40 flex items-center justify-between border-b border-line bg-white/95 px-4 py-3 backdrop-blur-xl lg:hidden">
        <div>
          <div className="font-display text-base font-extrabold tracking-[-0.03em] text-ink">
            brickspace<span className="text-brand">.</span>
          </div>
          <div className="mt-0.5 text-xs text-muted2">{user?.name}</div>
        </div>
        <button
          onClick={doLogout}
          data-testid="logout-btn-mobile"
          className="rounded-lg border border-line p-2 text-body"
        >
          <LogOut className="h-4 w-4" />
        </button>
      </header>

      <main className="pb-24 lg:ml-60 lg:pb-10">
        <div className="mx-auto max-w-[1400px] px-4 py-6 lg:px-8 lg:py-9">
          <div className="mb-7 flex flex-wrap items-end justify-between gap-4">
            <div>
              <h1 className="font-display text-2xl font-extrabold tracking-[-0.02em] sm:text-3xl">
                {title}
              </h1>
              {subtitle && <p className="mt-1.5 text-sm text-body">{subtitle}</p>}
            </div>
            {actions}
          </div>
          {children}
        </div>
      </main>

      <nav className="fixed inset-x-0 bottom-0 z-40 flex border-t border-line bg-white/95 backdrop-blur-xl lg:hidden">
        {items.slice(0, 5).map((n) => {
          const active = pathname === n.to;
          return (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`${n.testid}-mobile`}
              className={`flex flex-1 flex-col items-center gap-1 py-2.5 text-[10px] font-semibold ${
                active ? "text-brand" : "text-muted2"
              }`}
            >
              <n.icon className="h-4 w-4" />
              {n.label}
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
